import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  ModalFooter,
  SimpleGrid,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import auth from '../../../apis/auth';
import useDebounce from '../../../hooks/useDebounce';
import useMutacoesPageGroup from '../../../hooks/useMutacoesPageGroup';
import redirectTypes from '../../../utils/redirectTypes';
import montarSchemaAgendamento from '../../../utils/schemaAgendamento';
import sanitizeString from '../../../utils/sanitizeString';
import Datepicker from '../../Form/Datepicker';
import Input from '../../Form/Input';
import Select from '../../Form/Select';
import { COR_DE_FUNDO_PADRAO } from '../../MobileComponents/vitrines/constantes';
import context from '../context';

interface Props {
  onCreated?: (data: any) => void;
  /** Na Vitrine com Cronômetro a data final alimenta o contador, então o agendamento é obrigatório. */
  agendamentoObrigatorio?: boolean;
}

type Opcao = { label: string; value: string };

interface FormData {
  name?: string;
  background_color?: string;
  reference_id: Opcao;
  action_text?: string;
  redirect_type?: Opcao;
  redirect_value?: string;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
}

const montarSchema = (agendamentoObrigatorio: boolean) =>
  yup.object().shape({
    name: yup.string().nullable(),
    background_color: yup.string().nullable(),
    action_text: yup.string().nullable(),
    redirect_value: yup.string().nullable(),
    reference_id: yup.object().shape({
      value: yup.string().required('Escolha uma coleção'),
      label: yup.string().required('Escolha uma coleção'),
    }),
    ...montarSchemaAgendamento(agendamentoObrigatorio),
  });

const FormVitrine: React.FC<Props> = ({
  onCreated,
  agendamentoObrigatorio = false,
}) => {
  const toast = useToast();
  const { pageGroup, newPageGroupDTO, onPageGroupCreated, onClose, display } =
    React.useContext(context);

  const { formState, handleSubmit, register, control, setValue, watch } =
    useForm<FormData>({
      resolver: yupResolver(montarSchema(agendamentoObrigatorio)),
    });

  const { debounce, reset } = useDebounce();
  const [colecoes, setColecoes] = useState<Opcao[]>([]);
  const [buscandoColecao, setBuscandoColecao] = useState(false);
  const [schedule, setSchedule] = useState(
    agendamentoObrigatorio || !!pageGroup?.schedule_end,
  );

  const {
    createPageGroup,
    updatePageGroup,
    removePageGroup,
    salvando,
    excluindo,
    avisarErroInesperado,
  } = useMutacoesPageGroup();

  const { errors } = formState;

  useEffect(() => {
    if (!pageGroup) {
      setValue('background_color', COR_DE_FUNDO_PADRAO);
      return;
    }

    setValue('name', pageGroup?.name);
    setValue(
      'background_color',
      pageGroup?.background_color || COR_DE_FUNDO_PADRAO,
    );
    setValue('action_text', pageGroup?.action_text);
    setValue('redirect_value', pageGroup?.redirect_value);
    setValue(
      'redirect_type',
      redirectTypes.find(t => t.value === pageGroup?.redirect_type) as any,
    );
    // A colecao volta do banco so como id; o rotulo real aparece quando o
    // operador buscar de novo. Mostrar o id evita um campo em branco, que
    // pareceria "sem colecao configurada".
    if (pageGroup?.reference_id) {
      setValue('reference_id', {
        label: `Coleção ${pageGroup.reference_id}`,
        value: pageGroup.reference_id,
      });
    }
    if (pageGroup?.schedule_end) {
      setValue('schedule', true);
      setValue('schedule_start', new Date(pageGroup.schedule_start));
      setValue('schedule_end', new Date(pageGroup.schedule_end));
    }
  }, [pageGroup, setValue]);

  const buscarColecoes = async (termo: string) => {
    try {
      setBuscandoColecao(true);
      const { data } = await auth.get<any>(
        `bridge/vtex/api/catalog_system/pvt/collection/search/${encodeURIComponent(
          termo,
        )}`,
      );
      setColecoes(
        (data?.items ?? []).map((c: any) => ({
          value: String(c?.id),
          label: `${c?.name} (${c?.id})`,
        })),
      );
    } catch (err: any) {
      if (err?.response?.status !== 404) {
        toast({
          title: 'Opss!!',
          description: 'Não foi possível buscar as coleções',
          status: 'error',
          position: 'top-right',
          duration: 1500,
        });
      }
      setColecoes([]);
    } finally {
      setBuscandoColecao(false);
    }
  };

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);
      const { schedule: scheduleData, ...rest } = sanitizedObj;

      const dadosDoGrupo = {
        name: rest.name,
        background_color: rest.background_color || null,
        action_text: rest.action_text || null,
        reference_id: data.reference_id?.value,
        redirect_type: data.redirect_type?.value || null,
        redirect_value: rest.redirect_value || null,
        schedule_end:
          scheduleData || agendamentoObrigatorio ? rest.schedule_end : null,
        schedule_start:
          scheduleData || agendamentoObrigatorio
            ? rest.schedule_start
            : new Date(),
      };

      if (pageGroup) {
        await updatePageGroup({
          variables: {
            updatePageGroupInput: { ...dadosDoGrupo, id: pageGroup?.id },
          },
        });
        toast({
          title: 'Sucesso!',
          description: 'Vitrine editada com sucesso!',
          status: 'success',
          position: 'top-right',
        });
        return;
      }

      const apiData = await createPageGroup({
        variables: {
          createPageGroupInput: {
            ...dadosDoGrupo,
            ...newPageGroupDTO,
            element_display_id: display?.id,
          },
        },
      });
      if (onCreated) onCreated(apiData);
      if (onPageGroupCreated) onPageGroupCreated(apiData);
      toast({
        title: 'Sucesso!',
        description: 'Vitrine criada com sucesso!',
        status: 'success',
        position: 'top-right',
      });
    } catch (erro) {
      avisarErroInesperado(erro);
    }
  };

  const handleDelete = async () => {
    try {
      if (!window.confirm('Tem certeza que deseja excluir a vitrine?')) return;
      await removePageGroup({ variables: { id: pageGroup?.id } });
      toast({
        title: 'Sucesso!',
        description: 'Vitrine deletada com sucesso',
        status: 'success',
        position: 'top-right',
      });
      if (onClose) onClose();
    } catch (erro) {
      avisarErroInesperado(erro);
    }
  };

  return (
    <>
      <VStack as="form" spacing="8" pb="6" onSubmit={handleSubmit(handleForm)}>
        <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
          <Input
            label="Título"
            placeholder="Ex: Ofertas da semana"
            error={errors?.name}
            {...register('name')}
          />
          <Select
            isRequired
            name="reference_id"
            label="Coleção VTEX"
            placeholder="Digite para buscar..."
            control={control}
            options={colecoes}
            error={errors?.reference_id}
            isLoading={buscandoColecao}
            onInputChange={(termo: string) => {
              setColecoes([]);
              if (!termo) {
                reset();
                setBuscandoColecao(false);
                return;
              }
              setBuscandoColecao(true);
              debounce(() => buscarColecoes(termo), 500);
            }}
          />
        </SimpleGrid>

        <Flex align="flex-end" gap={3} alignSelf="start">
          <Input
            label="Cor de fundo do container"
            type="color"
            width="120px"
            error={errors?.background_color}
            {...register('background_color')}
          />
          {/* o input nativo de cor nao mostra o valor escolhido */}
          <Text fontSize="sm" color="gray.600" pb="8px">
            {watch('background_color') || COR_DE_FUNDO_PADRAO}
          </Text>
        </Flex>

        <Text
          as="h2"
          fontSize="md"
          fontWeight="bold"
          textAlign="left"
          alignSelf="start"
        >
          Link &quot;Ver mais&quot; (opcional)
        </Text>

        <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
          <Input
            label="Texto do link"
            placeholder="Ex: Ver mais"
            error={errors?.action_text}
            {...register('action_text')}
          />
          <Select
            name="redirect_type"
            label="Tipo de redirecionamento"
            control={control}
            options={redirectTypes}
            error={errors?.redirect_type}
            placeholder="Selecione"
          />
          <Input
            label="Valor do redirecionamento"
            error={errors?.redirect_value}
            {...register('redirect_value')}
          />
        </SimpleGrid>

        {agendamentoObrigatorio ? (
          <Text
            as="h2"
            fontSize="md"
            fontWeight="bold"
            textAlign="left"
            alignSelf="start"
          >
            Período da oferta
          </Text>
        ) : (
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="schedule" mb="0">
              Agendamento
            </FormLabel>
            <Switch
              id="schedule"
              size="lg"
              colorScheme="brand"
              isChecked={schedule}
              {...register('schedule')}
              onChange={evento => {
                setSchedule(evento.target.checked);
                setValue('schedule', evento.target.checked);
              }}
            />
          </FormControl>
        )}

        {(schedule || agendamentoObrigatorio) && (
          <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
            <Datepicker
              isRequired
              control={control}
              {...register('schedule_start')}
              label="Início"
              showTimeSelect
              error={errors?.schedule_start}
            />
            <Datepicker
              isRequired
              control={control}
              {...register('schedule_end')}
              label="Final"
              showTimeSelect
              error={errors?.schedule_end}
            />
          </SimpleGrid>
        )}
      </VStack>

      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={excluindo}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>Fechar</Button>
          <Button
            colorScheme="brand"
            isLoading={salvando}
            onClick={handleSubmit(handleForm)}
          >
            Salvar
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

export default FormVitrine;
